import React, { useMemo, useState } from 'react';
import { Alert, Button, Collapse, Descriptions, Input, List, Popover, Space, Tag, Typography, message } from 'antd';
import { CommentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { bugReportApi } from '../../services/bugReportApi';
import { AcceptanceFieldRule, AcceptanceManifest } from './types';
import { useAcceptanceMode } from './AcceptanceModeContext';
import { AcceptanceChangeRequestButton } from './AcceptanceChangeRequestButton';

interface Props {
  manifest: AcceptanceManifest;
  fieldId: string;
  documentKey?: string;
  fieldOverride?: AcceptanceFieldRule;
}

export const AcceptanceFieldHint: React.FC<Props> = ({ manifest, fieldId, documentKey, fieldOverride }) => {
  const { isEnabled } = useAcceptanceMode();
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const field = useMemo(
    () => fieldOverride || manifest.fields.find(item => item.fieldId === fieldId),
    [fieldId, fieldOverride, manifest.fields],
  );

  if (!isEnabled(manifest.formId) || !field) return null;

  const submit = async () => {
    const description = comment.trim();
    if (!description) return;
    setSubmitting(true);
    try {
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      const parsed = storedUser ? JSON.parse(storedUser) : {};
      await bugReportApi.create({
        formId: manifest.formId,
        page: window.location.pathname,
        pageTitle: `${manifest.displayName}／${field.label}`,
        reportedBy: parsed.userName || parsed.userId || 'anonymous',
        browserInfo: navigator.userAgent,
        annotations: [{
          selector: `[data-acceptance-field="${field.fieldId}"]`,
          elementTag: 'acceptance-field',
          elementText: `${field.table}.${field.column}`,
          label: `驗收意見｜${field.label}`,
          description: `${description}\n文件：${documentKey || '(新增中)'}\nContract：${field.sourceRefs.join(', ')}`,
          rect: { x: 0, y: 0, width: 0, height: 0 },
        }],
      });
      setComment('');
      message.success('驗收意見已送出');
    } catch {
      message.error('驗收意見送出失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div style={{ width: 340 }} data-acceptance-field={field.fieldId}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>{field.label}</Typography.Title>
      {field.evidenceStatus === 'manual_review' && field.controlIds.length === 0 && <Alert
        type="warning"
        showIcon
        message="此畫面欄位尚未寫入 Contract"
        description="目前只能確認畫面上存在這個欄位；用途、允許值、檢核規則及資料庫對應仍待補齊，因此不可視為已確認。"
        style={{ marginBottom: 8 }}
      />}
      <Typography.Paragraph>{field.description}</Typography.Paragraph>
      <Space size={[4, 4]} wrap>
        {field.required && <Tag color="red">必填</Tag>}
        {field.readOnly && <Tag>唯讀</Tag>}
        {field.conditionallyMounted && <Tag color="blue">條件式顯示</Tag>}
        {field.maxLength && <Tag>最多 {field.maxLength} 字</Tag>}
        <Tag color={field.evidenceStatus === 'confirmed' ? 'green' : field.evidenceStatus === 'manual_review' ? 'orange' : 'gold'}>
          {field.evidenceStatus === 'confirmed' ? '來源已確認' : field.evidenceStatus === 'manual_review' ? '待人工確認' : '推定'}
        </Tag>
      </Space>
      {field.conditionallyMounted && field.mountCondition && (
        <Typography.Paragraph style={{ marginTop: 8, marginBottom: 4 }}>
          <strong>顯示條件：</strong>{field.mountCondition}
        </Typography.Paragraph>
      )}
      {field.allowedValues && field.allowedValues.length > 0 && (
        <Typography.Paragraph style={{ marginTop: 8, marginBottom: 4 }}>
          允許值：{field.allowedValues.map(item => (
            typeof item === 'string'
              ? item
              : `${item.value}（${item.label}${item.description ? `：${item.description}` : ''}）`
          )).join('、')}
        </Typography.Paragraph>
      )}
      {field.validationGuidance.length > 0
        ? <List
          size="small"
          dataSource={field.validationGuidance}
          renderItem={rule => <List.Item style={{ display: 'block', paddingInline: 0 }}>
            <Alert
              type="info"
              showIcon
              message={rule.summary}
              description={<>
                {rule.businessReason && <div><strong>為什麼：</strong>{rule.businessReason}</div>}
                {rule.validExample && <div><strong>可接受：</strong>{rule.validExample}</div>}
                {rule.invalidExample && <div><strong>不可接受：</strong>{rule.invalidExample}</div>}
                {rule.failureMessage && <div><strong>失敗提示：</strong>{rule.failureMessage}</div>}
                {rule.businessImpact && <div><strong>失敗結果：</strong>{rule.businessImpact}</div>}
              </>}
            />
          </List.Item>}
        />
        : <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>此欄位沒有額外的 Contract 檢核。</Typography.Paragraph>}
      <Collapse
        ghost
        size="small"
        items={[{
          key: 'technical',
          label: '查看技術依據',
          children: <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="資料庫欄位">
              {field.table === 'UNMODELED_UI' ? '尚未確認' : `${field.table}.${field.column}`}
            </Descriptions.Item>
            <Descriptions.Item label="欄位 Control">{field.controlIds.join('、') || '尚未建模'}</Descriptions.Item>
            <Descriptions.Item label="技術說明">{field.technicalDescription}</Descriptions.Item>
            <Descriptions.Item label="規則式">
              {field.validationGuidance.map(rule => <div key={rule.controlId}><code>{rule.controlId}: {rule.technicalExpression}</code></div>)}
            </Descriptions.Item>
            <Descriptions.Item label="來源">{field.sourceRefs.join('、')}</Descriptions.Item>
          </Descriptions>,
        }]}
      />
      <Input.TextArea
        value={comment}
        onChange={event => setComment(event.target.value)}
        placeholder="對這個欄位留下錯誤或改善意見"
        rows={2}
        style={{ marginTop: 10 }}
      />
      <Button type="primary" size="small" icon={<CommentOutlined />} loading={submitting} disabled={!comment.trim()} onClick={submit} style={{ marginTop: 8 }}>
        送出欄位意見
      </Button>
      <AcceptanceChangeRequestButton
        manifest={manifest}
        contextType="field"
        fieldId={field.fieldId}
        controlId={field.controlIds[0]}
        documentKey={documentKey}
        inputSummary={{ table: field.table, column: field.column }}
        label="提出欄位 Contract 變更"
      />
    </div>
  );

  return (
    <Popover content={content} trigger="click">
      <span
        data-acceptance-hint-field={field.fieldId}
        aria-label={`${field.label}欄位說明`}
        role="button"
        tabIndex={0}
        style={{ display: 'inline-flex', alignItems: 'center' }}
      >
        <InfoCircleOutlined style={{ marginLeft: 4, color: '#1677ff', cursor: 'pointer' }} />
      </span>
    </Popover>
  );
};
